// dashboard/frontend/src/components/CopytradeDiscovery.js
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCopytradeCandidates,
  acceptCandidate,
  rejectCandidate,
  getCopytradeConfig,
  putCopytradeConfig,
  runCopytradeDiscovery,
  getDiscoveryStatus,
  deactivateCopytradeWallet,
  getCopiedWallets,
} from '../services/api';

const CONFIG_FIELDS = [
  { key: 'min_trades', label: 'min_trades', type: 'number' },
  { key: 'top', label: 'top', type: 'number' },
  { key: 'min_win_rate', label: 'min_win_rate', type: 'number' },
  { key: 'sol_per_day_min', label: 'sol_per_day_min', type: 'number' },
  { key: 'n_self_creates_max', label: 'n_self_creates_max', type: 'number' },
  { key: 'n_token_transfers_in_max', label: 'n_token_transfers_in_max', type: 'number' },
  { key: 'n_tokens_min', label: 'n_tokens_min', type: 'number' },
  { key: 'elite_win_rate', label: 'elite_win_rate', type: 'number' },
  { key: 'elite_sol_per_day', label: 'elite_sol_per_day', type: 'number' },
  { key: 'anti_scanner_enabled', label: 'anti_scanner_enabled', type: 'boolean' },
];

function fmtAddr(addr) {
  if (!addr) return '—';
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

function fmt(v, d = 2) {
  if (v === null || v === undefined) return '—';
  return Number(v).toFixed(d);
}

export default function CopytradeDiscovery() {
  const qc = useQueryClient();
  const [err, setErr] = useState(null);
  const [acceptState, setAcceptState] = useState({}); // { wallet: { open, mode, size_mult, size_cap_sol } }
  const [cfgDraft, setCfgDraft] = useState(null);

  const candidatesQ = useQuery({ queryKey: ['ct-candidates'], queryFn: getCopytradeCandidates, refetchInterval: 30000 });
  const statusQ = useQuery({
    queryKey: ['ct-discovery-status'],
    queryFn: getDiscoveryStatus,
    refetchInterval: (data) => (data?.status === 'running' ? 3000 : 15000),
  });
  const cfgQ = useQuery({
    queryKey: ['ct-config'],
    queryFn: getCopytradeConfig,
    onSuccess: (d) => { if (!cfgDraft) setCfgDraft(d); },
  });
  const walletsQ = useQuery({
    queryKey: ['ct-active-wallets'],
    queryFn: () => getCopiedWallets({ activeOnly: true, limit: 100 }),
    refetchInterval: 30000,
  });

  const cfg = cfgQ.data || {};
  const candidates = candidatesQ.data?.candidates || [];
  const statusData = statusQ.data || {};
  const activeWallets = walletsQ.data?.wallets || [];

  const onErr = (e) => setErr(e?.response?.data?.detail || e?.message || 'Erreur inconnue');

  const mAccept = useMutation({
    mutationFn: ({ wallet, body }) => acceptCandidate(wallet, body),
    onSuccess: (_, { wallet }) => {
      setErr(null);
      setAcceptState((s) => ({ ...s, [wallet]: { open: false, mode: 'observe', size_mult: '', size_cap_sol: '' } }));
      qc.invalidateQueries({ queryKey: ['ct-candidates'] });
      qc.invalidateQueries({ queryKey: ['ct-active-wallets'] });
      qc.invalidateQueries({ queryKey: ['copied-wallets'] });
    },
    onError: onErr,
  });

  const mReject = useMutation({
    mutationFn: (wallet) => rejectCandidate(wallet),
    onSuccess: () => {
      setErr(null);
      qc.invalidateQueries({ queryKey: ['ct-candidates'] });
    },
    onError: onErr,
  });

  const mRun = useMutation({
    mutationFn: runCopytradeDiscovery,
    onSuccess: () => {
      setErr(null);
      qc.invalidateQueries({ queryKey: ['ct-discovery-status'] });
    },
    onError: onErr,
  });

  const mCfg = useMutation({
    mutationFn: putCopytradeConfig,
    onSuccess: () => {
      setErr(null);
      qc.invalidateQueries({ queryKey: ['ct-config'] });
    },
    onError: onErr,
  });

  const mDeactivate = useMutation({
    mutationFn: (wallet) => deactivateCopytradeWallet(wallet),
    onSuccess: () => {
      setErr(null);
      qc.invalidateQueries({ queryKey: ['ct-active-wallets'] });
      qc.invalidateQueries({ queryKey: ['copied-wallets'] });
    },
    onError: onErr,
  });

  // Accept popover helpers — ouvre en pré-remplissant la taille recommandée (rec_*)
  const openAccept = (c) =>
    setAcceptState((s) => ({
      ...s,
      [c.wallet]: {
        open: true,
        mode: 'observe',
        size_mult: c.rec_size_mult != null ? String(c.rec_size_mult) : '',
        size_cap_sol: c.rec_size_cap_sol != null ? String(c.rec_size_cap_sol) : '',
      },
    }));
  const closeAccept = (wallet) =>
    setAcceptState((s) => ({ ...s, [wallet]: { ...s[wallet], open: false } }));
  const patchAccept = (wallet, patch) =>
    setAcceptState((s) => ({ ...s, [wallet]: { ...s[wallet], ...patch } }));

  // Armement direct depuis la liste avec la taille recommandée (1 clic).
  const doArmReco = (c) =>
    mAccept.mutate({
      wallet: c.wallet,
      body: {
        mode: 'armed',
        size_mult: c.rec_size_mult != null ? Number(c.rec_size_mult) : undefined,
        size_cap_sol: c.rec_size_cap_sol != null ? Number(c.rec_size_cap_sol) : undefined,
      },
    });

  const doAccept = (wallet) => {
    const st = acceptState[wallet] || {};
    const body = { mode: st.mode || 'observe' };
    if (st.mode === 'armed') {
      if (st.size_mult !== '' && st.size_mult != null) body.size_mult = Number(st.size_mult);
      if (st.size_cap_sol !== '' && st.size_cap_sol != null) body.size_cap_sol = Number(st.size_cap_sol);
    }
    mAccept.mutate({ wallet, body });
  };

  // Config draft
  const initDraft = () => {
    if (!cfgDraft && cfg && Object.keys(cfg).length) setCfgDraft({ ...cfg });
  };
  const onCfgChange = (key, value) => {
    initDraft();
    setCfgDraft((d) => ({ ...d, [key]: value }));
  };
  const saveCfg = () => {
    if (cfgDraft) mCfg.mutate(cfgDraft);
  };

  const draftOrCfg = cfgDraft || cfg;

  const statusColor = statusData.status === 'running' ? 'var(--cyan)' : statusData.status === 'done' ? 'var(--green)' : 'var(--text2)';

  return (
    <div className="cs-wrap">
      {err && <div className="cs-err" style={{ marginBottom: 10 }}>Erreur : {err}</div>}

      {/* Section 1 : File des candidats */}
      <div className="h1" style={{ marginBottom: 10 }}>Candidats ({candidates.length})</div>
      {candidatesQ.isLoading && <div style={{ color: 'var(--text2)', marginBottom: 12 }}>Chargement...</div>}
      {candidates.length === 0 && !candidatesQ.isLoading && (
        <div style={{ color: 'var(--text2)', marginBottom: 16, fontSize: 12 }}>
          Aucun candidat — lancer la découverte ci-dessous.
        </div>
      )}
      {candidates.length > 0 && (
        <div style={{ overflowX: 'auto', marginBottom: 16 }}>
          <table className="disc-tbl">
            <thead>
              <tr>
                <th>Wallet</th>
                <th>Win rate</th>
                <th>Real-peak</th>
                <th>Net SOL</th>
                <th>SOL/j</th>
                <th>Tokens/j</th>
                <th>N tokens</th>
                <th>Tier</th>
                <th>Taille reco</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((c) => {
                const st = acceptState[c.wallet] || { open: false, mode: 'observe', size_mult: '', size_cap_sol: '' };
                return (
                  <tr key={c.wallet}>
                    <td className="cs-slot" title={c.wallet}>{fmtAddr(c.wallet)}</td>
                    <td>{c.win_rate != null ? `${(c.win_rate * 100).toFixed(0)}%` : '—'}</td>
                    <td>
                      {c.realpeak_rate != null ? `${(c.realpeak_rate * 100).toFixed(0)}%` : '—'}
                      {c.bc_hidden && (
                        <span title="Rentable sur vrais pumps mais négatif au ledger bonding-curve — invisible à l'ancien gate" style={{ marginLeft: 4, cursor: 'help' }}>🔓</span>
                      )}
                    </td>
                    <td>{c.net_sol != null ? <span className={c.net_sol >= 0 ? '' : 'cs-err'}>{c.net_sol > 0 ? '+' : ''}{fmt(c.net_sol)}</span> : '—'}</td>
                    <td>{fmt(c.sol_per_day)}</td>
                    <td>{fmt(c.tokens_per_day, 1)}</td>
                    <td>{c.n_tokens ?? '—'}</td>
                    <td><span className="cs-badge" data-ev={c.tier === 'elite' ? 'live' : 'batch'}>{c.tier || 'standard'}</span></td>
                    <td className="cs-slot" title={c.rec_reason || ''}>
                      {c.rec_size_mult != null ? `${Number(c.rec_size_mult).toFixed(1)}× / ${Number(c.rec_size_cap_sol).toFixed(2)}` : '—'}
                    </td>
                    <td>
                      {!st.open ? (
                        <>
                          <button className="tdp-btn on" style={{ marginRight: 4 }} onClick={() => openAccept(c)}>Accepter</button>
                          {c.rec_size_mult != null && (
                            <button
                              className="tdp-btn on"
                              style={{ marginRight: 4 }}
                              title={`Armer directement : ${c.rec_reason || ''}`}
                              onClick={() => doArmReco(c)}
                              disabled={mAccept.isPending}
                            >
                              Armer reco
                            </button>
                          )}
                          <button className="tdp-btn" onClick={() => mReject.mutate(c.wallet)} disabled={mReject.isPending}>Rejeter</button>
                        </>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <select
                            className="arm-in"
                            value={st.mode}
                            onChange={(e) => patchAccept(c.wallet, { mode: e.target.value })}
                          >
                            <option value="observe">observe</option>
                            <option value="armed">armé</option>
                          </select>
                          {st.mode === 'armed' && (
                            <>
                              <input
                                className="arm-in"
                                type="number"
                                placeholder="size_mult"
                                value={st.size_mult}
                                style={{ width: 90 }}
                                onChange={(e) => patchAccept(c.wallet, { size_mult: e.target.value })}
                              />
                              <input
                                className="arm-in"
                                type="number"
                                placeholder="cap SOL"
                                value={st.size_cap_sol}
                                style={{ width: 80 }}
                                onChange={(e) => patchAccept(c.wallet, { size_cap_sol: e.target.value })}
                              />
                            </>
                          )}
                          <button className="tdp-btn on" onClick={() => doAccept(c.wallet)} disabled={mAccept.isPending}>OK</button>
                          <button className="tdp-btn" onClick={() => closeAccept(c.wallet)}>Annuler</button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Section 2 : Lancer la decouverte + statut */}
      <div className="h1" style={{ marginBottom: 8 }}>Lancer la découverte</div>
      <div className="arm-bar" style={{ marginBottom: 16 }}>
        <button
          className="tdp-btn on"
          onClick={() => mRun.mutate()}
          disabled={mRun.isPending || statusData.status === 'running'}
        >
          {statusData.status === 'running' ? 'En cours...' : 'Lancer la découverte'}
        </button>
        {statusData.status && (
          <span style={{ fontSize: 11, color: statusColor }}>
            Statut : <b>{statusData.status}</b>
            {statusData.started_at && <span className="cs-slot"> · démarré {statusData.started_at}</span>}
            {statusData.ended_at && <span className="cs-slot"> · terminé {statusData.ended_at}</span>}
            {statusData.n_candidates != null && <span className="cs-slot"> · {statusData.n_candidates} candidats</span>}
            {statusData.error && <span style={{ color: 'var(--red)', marginLeft: 6 }}>{statusData.error}</span>}
          </span>
        )}
      </div>

      {/* Section 3 : Seuils de configuration */}
      <div className="h1" style={{ marginBottom: 8 }}>Seuils de découverte</div>
      {cfgQ.isLoading && <div style={{ color: 'var(--text2)', marginBottom: 12 }}>Chargement...</div>}
      {!cfgQ.isLoading && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8, marginBottom: 10 }}>
            {CONFIG_FIELDS.map((f) => (
              <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="cs-slot" style={{ minWidth: 160, fontSize: 10 }}>{f.key}</span>
                {f.type === 'boolean' ? (
                  <input
                    type="checkbox"
                    checked={!!draftOrCfg[f.key]}
                    onChange={(e) => onCfgChange(f.key, e.target.checked)}
                  />
                ) : (
                  <input
                    className="arm-in"
                    type="number"
                    value={draftOrCfg[f.key] ?? ''}
                    style={{ width: 80 }}
                    onChange={(e) => onCfgChange(f.key, e.target.value === '' ? null : Number(e.target.value))}
                  />
                )}
              </div>
            ))}
          </div>
          {mCfg.isError && (
            <div style={{ color: 'var(--red)', fontSize: 11, marginBottom: 6 }}>
              Erreur 422 : {mCfg.error?.response?.data?.detail || mCfg.error?.message}
            </div>
          )}
          <button className="tdp-btn on" onClick={saveCfg} disabled={mCfg.isPending}>Enregistrer</button>
          {mCfg.isSuccess && <span style={{ color: 'var(--green)', fontSize: 11, marginLeft: 8 }}>Enregistré</span>}
        </div>
      )}

      {/* Section 4 : Wallets actifs */}
      <div className="h1" style={{ marginBottom: 8 }}>Wallets actifs ({activeWallets.length})</div>
      {walletsQ.isLoading && <div style={{ color: 'var(--text2)', marginBottom: 12 }}>Chargement...</div>}
      {!walletsQ.isLoading && activeWallets.length === 0 && (
        <div style={{ color: 'var(--text2)', fontSize: 12, marginBottom: 12 }}>Aucun wallet actif.</div>
      )}
      {activeWallets.length > 0 && (
        <div style={{ overflowX: 'auto', marginBottom: 16 }}>
          <table className="disc-tbl">
            <thead>
              <tr>
                <th>Wallet</th>
                <th>Win rate</th>
                <th>Net SOL</th>
                <th>SOL/j</th>
                <th>Tier</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {activeWallets.map((w) => (
                <tr key={w.wallet}>
                  <td className="cs-slot" title={w.wallet}>{fmtAddr(w.wallet)}</td>
                  <td>{w.win_rate != null ? `${w.win_rate.toFixed(1)}%` : '—'}</td>
                  <td>{w.net_sol != null ? <span className={w.net_sol >= 0 ? '' : 'cs-err'}>{w.net_sol > 0 ? '+' : ''}{fmt(w.net_sol)}</span> : '—'}</td>
                  <td>{fmt(w.sol_per_day)}</td>
                  <td><span className="cs-badge" data-ev={w.tier === 'elite' ? 'live' : 'batch'}>{w.tier || 'standard'}</span></td>
                  <td>
                    <button
                      className="tdp-btn"
                      onClick={() => mDeactivate.mutate(w.wallet)}
                      disabled={mDeactivate.isPending}
                    >
                      Désactiver
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
